// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { RouterEventsService } from 'src/app/core/services/router-events/router-events.service';
import { fromCarl } from 'src/app/store/common/common.selectors';
import { filterChanged } from 'src/app/utils/functions/rx-filters';

interface FootnoteMeta {
  count: number;
  hint: string;
  context?: string;
  section: string;
}

export interface Footnote {
  hint: string;
  text: string;
}

export interface FootnoteWithOccuranceCounter {
  text: string;
  counter: number;
}

/**
 * The FootnoteService holds a collection of footnote hints within the current
 * page. It subscribes to routing events to resets the collection.
 * Footnote meta collection can optionally be filtered by section name.
 * The global section contains all footnote meta from all sections.
 */
@Injectable({
  providedIn: 'root'
})
export class FootnotesService {
  public readonly globalContext = 'global';
  public readonly globalSection = 'footer';

  /**
   * The footnotes subject holds for each footnote id the count of its
   * appearances within the current page.
   */
  private readonly footnotesSubject = new BehaviorSubject<Map<string, FootnoteMeta>>(new Map<string, FootnoteMeta>());

  /**
   * The footnotesStore subject holds for registered footnotes. Seperated by context like 'global, finance...'
   * e.g.
   * {
   *   'global' : {
   *     '1' : 'FootnoteText1',
   *     '2' : 'FootnoteText2'
   *    },
   *    'finance' : {
   *      'a' : 'FootnoteTexta'
   *    }
   * }
   */
  private readonly footnotesStore = new Map<string, Map<string, FootnoteWithOccuranceCounter>>();

  public constructor(
    private readonly routerEventsService: RouterEventsService,
    private readonly store: Store
  ) {
    this.routerEventsService.navigationEnd$.subscribe(() => {
      const footnotes = this.footnotesSubject.getValue();
      footnotes.clear();
      this.footnotesSubject.next(footnotes);
    });

    // store subscription exception:
    // - for this functionality, there is no way to subscribe to the store in a template.
    // - the service is provided in root and the subscription is unsubscribed on app destroy.
    // eslint-disable-next-line @ngrx/no-store-subscription
    this.store.select(fromCarl.selectMultipleTranslations<Record<string, string>>('footnotes')).subscribe(footnotes => {
      for (const hint of Object.keys(footnotes || [])) {
        this.registerFootnote(hint, footnotes[hint]);
      }
    });
  }

  public addFootnote(hint: string, context: string = this.globalContext, section: string = this.globalSection): void {
    const footnotes = this.footnotesSubject.getValue();
    const ref = this.getFootnoteRef(hint, context, section);
    const meta: FootnoteMeta = footnotes.get(ref) || ({ hint, context, section } as FootnoteMeta);
    const count = Object.prototype.hasOwnProperty.call(meta, 'count') ? meta.count + 1 : 1;
    footnotes.set(ref, { ...meta, count });
    this.footnotesSubject.next(footnotes);
  }

  public removeFootnote(
    footnoteId: string,
    context: string = this.globalContext,
    section: string = this.globalSection
  ): void {
    const footnotes = this.footnotesSubject.getValue();
    const ref = this.getFootnoteRef(footnoteId, context, section);
    const meta = footnotes.get(ref);
    if (!meta) {
      return;
    }
    meta.count--;
    if (meta.count <= 0) {
      footnotes.delete(ref);
    } else {
      footnotes.set(ref, { ...meta });
    }
    this.footnotesSubject.next(footnotes);
  }

  public getFootnotes(section: string = this.globalSection): Observable<Footnote[]> {
    return this.footnotesSubject.pipe(
      map(footnotesMap => {
        // get array of current footnote hints
        let footnoteMetas: FootnoteMeta[] = [...footnotesMap.values()]
          // include hints for given section or if global section, return hints from all sections

          .filter(meta => meta.section === section)
          .map(meta => meta);

        if (section === this.globalSection) {
          // remove duplicates (if a hint was used in multiple sections)
          footnoteMetas = [...new Set(footnoteMetas).values()];
        }

        return footnoteMetas.map(
          meta =>
            ({
              hint: meta.hint,
              text: this.getFootnote(meta.hint, meta.context).text
            }) as Footnote
        );
      }),
      filterChanged()
    );
  }

  public registerFootnote(hint: string, text: string, context: string = this.globalContext): void {
    const contextFootnotes = this.footnotesStore.get(context) || new Map<string, FootnoteWithOccuranceCounter>();
    contextFootnotes.set(hint, { text, counter: 0 });
    this.footnotesStore.set(context, contextFootnotes);
  }

  // returns the footnote text for a given hint and the occurance counter, updates the counter
  public getFootnote(hint: string, context: string = this.globalContext): FootnoteWithOccuranceCounter {
    const footnoteContext = this.footnotesStore.get(context) || new Map<string, FootnoteWithOccuranceCounter>();
    const text = footnoteContext.get(hint)?.text || '';
    const counter = footnoteContext.get(hint)?.counter || 0;
    const newCounter = counter + 1;
    this.footnotesStore.set(context, footnoteContext.set(hint, { text, counter: newCounter }));
    return { text, counter };
  }

  private getFootnoteRef(footnoteId: string, context: string, section: string): string {
    return `${footnoteId}#${context}#${section || this.globalSection}`;
  }
}
